import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, X, ChevronLeft, ChevronRight, Camera } from 'lucide-react';

const MEDIA_ITEMS = [
  {
    id: 1,
    src: 'https://customer-assets.emergentagent.com/job_thailandrealty/artifacts/ejrcfdf8_S__7282706.jpg',
    title: 'NOK Thailand Property & Investment Launch',
    description: 'Announcing our real estate & hospitality investment platform across Pattaya, Bangkok, and Chiang Mai.',
    category: 'Launch Event',
  },
  {
    id: 2,
    src: 'https://customer-assets.emergentagent.com/job_thailandrealty/artifacts/nqu1inzn_S__7282695.jpg',
    title: 'NOK Platform — Full Coverage',
    description: 'Residential sales, luxury rentals, land acquisition, and hotel development partnerships across Thailand.',
    category: 'Promotional',
  },
  {
    id: 3,
    src: 'https://customer-assets.emergentagent.com/job_thailandrealty/artifacts/ve19d9kc_S__7282694.jpg',
    title: 'Investment Services Overview',
    description: 'Insurance, wealth protection, and legal advisory services for long-term retirees in Thailand.',
    category: 'Promotional',
  },
  {
    id: 4,
    src: 'https://customer-assets.emergentagent.com/job_thailandrealty/artifacts/cy30upvj_S__7381029.jpg',
    title: 'Meet Our Advisors — Nok & Pook',
    description: 'Your trusted legal and financial advisors providing expert guidance for property investors.',
    category: 'Team',
  },
  {
    id: 5,
    src: 'https://customer-assets.emergentagent.com/job_thailandrealty/artifacts/vcu6s665_S__7364680.jpg',
    title: 'Peace Heaven — Private Luxury Pool Villa',
    description: 'Featured luxury villa rental in Sam Roi Yot, Prachuap Khiri Khan. Perfect for retreats and long stays.',
    category: 'Featured Property',
  },
];

const CATEGORIES = ['All', 'Launch Event', 'Promotional', 'Team', 'Featured Property'];

const MediaPage = () => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const filtered = selectedCategory === 'All'
    ? MEDIA_ITEMS
    : MEDIA_ITEMS.filter(item => item.category === selectedCategory);

  const openLightbox = (index) => setLightboxIndex(index);
  const closeLightbox = () => setLightboxIndex(null);

  const goNext = () => {
    if (lightboxIndex !== null) {
      setLightboxIndex((lightboxIndex + 1) % filtered.length);
    }
  };

  const goPrev = () => {
    if (lightboxIndex !== null) {
      setLightboxIndex((lightboxIndex - 1 + filtered.length) % filtered.length);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0E27]">
      {/* Header */}
      <header className="crystal-glass fixed top-0 left-0 right-0 z-50" data-testid="media-header">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex-shrink-0">
              <h1 className="font-heading text-xl md:text-2xl font-semibold text-[#D4AF37] tracking-tight">
                NOK <span className="text-white/40 font-light mx-1">|</span> <span className="text-white font-light text-base md:text-lg">Thailand Property</span>
              </h1>
            </Link>
            <Button
              onClick={() => navigate('/')}
              variant="ghost"
              className="text-[#94A3B8] hover:text-white hover:bg-white/5 rounded-full"
              data-testid="back-to-home-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-32 pb-12 px-6 lg:px-8" data-testid="media-hero">
        <div className="max-w-7xl mx-auto text-center">
          <p className="overline text-[#D4AF37] mb-4">Gallery</p>
          <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold text-white tracking-tight mb-6">
            Events & Media
          </h2>
          <p className="text-[#94A3B8] text-lg max-w-2xl mx-auto leading-relaxed">
            Highlights from our launches, promotions, and featured properties.
          </p>
        </div>
      </section>

      {/* Category Filter */}
      <section className="px-6 lg:px-8 mb-12" data-testid="media-category-filter">
        <div className="max-w-7xl mx-auto flex flex-wrap gap-2 justify-center">
          {CATEGORIES.map((cat) => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={`px-5 py-2 rounded-full text-xs font-medium transition-colors duration-300 ${
                selectedCategory === cat
                  ? 'bg-[#D4AF37] text-[#0A0E27]'
                  : 'bg-white/5 text-[#94A3B8] hover:bg-white/10 hover:text-white border border-white/5'
              }`}
              data-testid={`media-cat-${cat.toLowerCase().replace(/\s+/g, '-')}`}
            >
              {cat}
            </button>
          ))}
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="px-6 lg:px-8 pb-24" data-testid="media-gallery">
        <div className="max-w-7xl mx-auto">
          {filtered.length === 0 ? (
            <div className="text-center py-20 bg-[#131A38] rounded-2xl border border-white/5">
              <Camera className="w-12 h-12 mx-auto text-[#D4AF37]/40 mb-4" />
              <p className="text-[#94A3B8]">No media in this category yet.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((item, index) => (
                <div
                  key={item.id}
                  className="property-card bg-[#131A38] rounded-2xl overflow-hidden border border-white/5 hover:border-[#D4AF37]/30 cursor-pointer group"
                  onClick={() => openLightbox(index)}
                  data-testid={`media-item-${item.id}`}
                >
                  <div className="relative h-64 overflow-hidden">
                    <img
                      src={item.src}
                      alt={item.title}
                      className="property-image w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#131A38] via-transparent to-transparent opacity-60" />
                    <span className="absolute top-4 left-4 px-3 py-1 bg-[#D4AF37]/90 text-[#0A0E27] text-[10px] font-semibold rounded-full uppercase tracking-wider">
                      {item.category}
                    </span>
                  </div>
                  <div className="p-5">
                    <h4 className="font-heading text-lg font-semibold text-white mb-1.5 group-hover:text-[#D4AF37] transition-colors duration-300">
                      {item.title}
                    </h4>
                    <p className="text-[#94A3B8] text-sm leading-relaxed line-clamp-2">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      {lightboxIndex !== null && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center"
          onClick={closeLightbox}
          data-testid="media-lightbox"
        >
          {/* Close */}
          <button
            className="absolute top-6 right-6 text-white/60 hover:text-white z-10 p-2"
            onClick={closeLightbox}
            data-testid="lightbox-close"
          >
            <X className="w-6 h-6" />
          </button>

          {/* Prev */}
          <button
            className="absolute left-4 md:left-8 text-white/40 hover:text-white z-10 p-2"
            onClick={(e) => { e.stopPropagation(); goPrev(); }}
            data-testid="lightbox-prev"
          >
            <ChevronLeft className="w-8 h-8" />
          </button>

          {/* Image */}
          <div
            className="max-w-4xl max-h-[85vh] mx-12 flex flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={filtered[lightboxIndex].src}
              alt={filtered[lightboxIndex].title}
              className="max-h-[70vh] w-auto rounded-xl object-contain"
            />
            <div className="mt-6 text-center">
              <h4 className="font-heading text-xl text-white mb-1">{filtered[lightboxIndex].title}</h4>
              <p className="text-[#94A3B8] text-sm">{filtered[lightboxIndex].description}</p>
            </div>
          </div>

          {/* Next */}
          <button
            className="absolute right-4 md:right-8 text-white/40 hover:text-white z-10 p-2"
            onClick={(e) => { e.stopPropagation(); goNext(); }}
            data-testid="lightbox-next"
          >
            <ChevronRight className="w-8 h-8" />
          </button>

          {/* Counter */}
          <div className="absolute bottom-6 text-[#94A3B8] text-sm">
            {lightboxIndex + 1} / {filtered.length}
          </div>
        </div>
      )}
    </div>
  );
};

export default MediaPage;
